import { FC, useEffect, useState } from "react";
import { Stack, useTheme, Box } from "@mui/material";
import {
  FormikCheckbox,
  FormikSelectField,
  FormikSelectSearchField,
  TableToggleField,
  DateRangePicker
  // FormikToggleField, // TODO will be added later
} from "@vilocnv/allsetra-core";
import { filterObjectsByGroup, removeDuplicateObjects } from "app/data/helpers";
import TimeFilters from "../../TimeFilters/TimeFilters";
import DaysFilters from "../../DaysFilter/DaysFilter";
import { RIDE_MODES } from "app/data/constants";
import { isEmpty } from "lodash";
import { FormLabelText, LabelRowBox } from "./InnerForm.styled";
import { useTranslation } from "react-i18next";

interface Values {
  group: string[];
  object: string[];
  objectIds?: string[];
  assignedToAllObjects: boolean;
}

interface Props {
  groups: Array<any>;
  objectTypes: Array<any>;
  objects: Array<any>;
  keys: Array<any>;
  dataLoading?: boolean;
  translator: any;
  fetchOnDebounce: (value: string) => void;
  onChange?: () => void;
  values: Values;
  setFieldValue: any;
  isCO2InnerForm?: boolean;
  isMultipleRidesForm?: boolean;
  isMileageReport?: boolean;
  reportType?: number;
  users?: Array<any>;
  showDateRangePicker?: boolean;
  dateValues?: any;
  handleDateChange?: (value: any) => void;
  dateRangeLoading?: boolean;
  driverIsRequired?: boolean;
}

const InnerForm: FC<Props> = ({
  groups,
  objectTypes,
  objects,
  keys,
  dataLoading = false,
  translator,
  fetchOnDebounce,
  onChange,
  values,
  setFieldValue,
  isCO2InnerForm = false,
  isMultipleRidesForm = false,
  isMileageReport = false,
  reportType = 2,
  users,
  showDateRangePicker = false,
  dateValues,
  handleDateChange,
  dateRangeLoading = false,
  driverIsRequired = true
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isSelectDaysAndTimeEnable, setIsSelectDaysAndTimeEnable] =
    useState(false);

  const handleToggleDaysAndTimeChange = (value: boolean) => {
    setIsSelectDaysAndTimeEnable(value);
  };

  useEffect(() => {
    const objectFieldName = reportType === 8 ? "objectIds" : "object";
    if (!values || !values[objectFieldName]) return;

    const filteredObjects: Array<any> =
      reportType === 8
        ? objects
        : filterObjectsByGroup(values, objects, groups);
    const uniqueFilteredObjects = removeDuplicateObjects(filteredObjects);
    const newObjectsValues = uniqueFilteredObjects
      .filter((item) => values[objectFieldName]?.includes(item.uniqueId))
      .map((item) => item.uniqueId);
    setFieldValue(objectFieldName, newObjectsValues);
  }, [values.group]);

  return (
    <Stack spacing={2}>
      {isMileageReport ? (
        <FormikSelectField
          theme={theme}
          label={translator("rideMode", { ns: "formFieldsTranslation" })}
          placeholder={translator("rideMode", { ns: "formFieldsTranslation" })}
          name="rideModes"
          options={RIDE_MODES(translator)}
          optionLabelKey="name"
          optionValueKey="value"
          multiple
          searchable
          fullWidth
        />
      ) : null}

      {showDateRangePicker && (
        <>
          <FormLabelText>{t("reports.selectDateRange")}</FormLabelText>
          <Box
            sx={{
              marginTop: "8px !important",
              marginBottom: "8px !important"
            }}
          >
            <DateRangePicker
              name="dateRange"
              onChange={(value) => {
                setFieldValue("dateRange", value);
                if (handleDateChange) {
                  handleDateChange(value);
                }
              }}
              value={dateValues}
              disabled={dateRangeLoading}
              theme={theme}
              translator={t}
            />
          </Box>
        </>
      )}

      <FormikCheckbox
        theme={theme}
        name="assignedToAllObjects"
        label={translator("selectAllObjects.label", {
          ns: "formFieldsTranslation"
        })}
      />

      {!values.assignedToAllObjects && (
        <FormikSelectSearchField
          theme={theme}
          label={translator("objects.label", { ns: "formFieldsTranslation" })}
          fetchOnDebounce={fetchOnDebounce}
          placeholder={translator("objects.placeholder", {
            ns: "formFieldsTranslation"
          })}
          name={reportType === 8 ? "objectIds" : "object"}
          options={
            reportType === 8
              ? objects
              : (filterObjectsByGroup(values, objects, groups) ?? [])
          }
          optionLabelKey="name"
          optionValueKey="uniqueId"
          loading={dataLoading}
          multiple
          searchable
          onChange={onChange}
          required={isMultipleRidesForm}
          fullWidth
          sx={{
            marginTop: "8px !important",
            "& .MuiAutocomplete-hasClearIcon.css-1x6bjyf-MuiAutocomplete-root .MuiOutlinedInput-root":
              {
                paddingRight: "9px"
              }
          }}
          //@ts-ignore
          showStates
          showDropdownIcon
        />
      )}

      {reportType === 8 && (
        <FormikSelectField
          theme={theme}
          label={translator("driver.label", {
            ns: "formFieldsTranslation"
          })}
          placeholder={translator("driver.placeholder", {
            ns: "formFieldsTranslation"
          })}
          name="driverId"
          options={users ?? []}
          optionLabelKey="email"
          optionValueKey="uniqueId"
          searchable
          onChange={onChange}
          required={driverIsRequired}
          fullWidth
        />
      )}

      {reportType !== 8 && (
        <FormikSelectField
          theme={theme}
          label={translator("objectType.label", {
            ns: "formFieldsTranslation"
          })}
          placeholder={translator("objectType.placeholder", {
            ns: "formFieldsTranslation"
          })}
          name="objectType"
          options={objectTypes ?? []}
          optionLabelKey="name"
          optionValueKey="uniqueId"
          loading={dataLoading}
          multiple
          searchable
          fullWidth
        />
      )}

      {groups?.length && reportType !== 8 ? (
        <FormikSelectField
          theme={theme}
          label={translator("groups.label", { ns: "formFieldsTranslation" })}
          placeholder={translator("groups.placeholder", {
            ns: "formFieldsTranslation"
          })}
          name="group"
          options={groups ?? []}
          optionLabelKey="name"
          optionValueKey="uniqueId"
          loading={dataLoading}
          multiple
          searchable
          fullWidth
        />
      ) : (
        <></>
      )}
      {!isEmpty(keys) && reportType !== 8 && (
        <FormikSelectField
          theme={theme}
          label={translator("keys.label", { ns: "formFieldsTranslation" })}
          placeholder={translator("keys.placeholder", {
            ns: "formFieldsTranslation"
          })}
          name="key"
          options={keys ?? []}
          optionLabelKey="label"
          optionValueKey="uniqueId"
          loading={dataLoading}
          multiple
          searchable
          fullWidth
        />
      )}

      {reportType !== 8 && (
        <TableToggleField
          label={
            translator("common.selectDaysAndTime") ?? "Select days and time"
          }
          name="selectDaysAndTime"
          onChange={(e) => handleToggleDaysAndTimeChange(e.target.checked)}
          checked={isSelectDaysAndTimeEnable}
        />
      )}

      {isSelectDaysAndTimeEnable && (
        <>
          <DaysFilters values={values} />

          <TimeFilters values={values} />
        </>
      )}

      {/* {!isCO2InnerForm && <DateAndTimeFilters values={values} />} */}

      {/* TODO will be aded later  */}
      {/* {isCO2InnerForm && (
        <>
          <RideModeFilters />
          <FormikToggleField
            label={translator("excludeRides.label", {
              ns: "formFieldsTranslation",
            })}
            name="excludeRides"
          />
        </>
      )} */}
    </Stack>
  );
};

export default InnerForm;
